/**
 * Notifications React Query Hooks
 * Provides data fetching hooks for notification inbox and unread counts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as notificationsApi from '../services/api/notifications';
import logger from '../utils/logger';

export const NOTIFICATIONS_KEYS = {
  all: ['notifications'],
  inbox: (params) => [...NOTIFICATIONS_KEYS.all, 'inbox', params],
  unreadCount: () => [...NOTIFICATIONS_KEYS.all, 'unread-count'],
};

/**
 * Hook to fetch notification inbox
 * @param {Object} params - { limit, offset, unreadOnly }
 * @param {Object} options - React Query options
 */
export function useNotificationInbox(params = {}, options = {}) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEYS.inbox(params),
    queryFn: () => notificationsApi.fetchNotificationInbox(params),
    staleTime: 30000, // 30 seconds
    retry: 1,
    ...options,
  });
}

/**
 * Hook to fetch unread notification count
 * Polls every 60 seconds for near-real-time badge updates
 * @param {Object} options - React Query options
 */
export function useUnreadNotificationCount(options = {}) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEYS.unreadCount(),
    queryFn: () => notificationsApi.fetchUnreadCount(),
    staleTime: 30000,
    refetchInterval: 60000, // Poll every 60s
    retry: 1,
    ...options,
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId) => notificationsApi.markNotificationRead(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      logger.info('[useNotifications] Notification marked as read');
    },
    onError: (error) => {
      logger.error('[useNotifications] Failed to mark as read', { error: error.message });
    },
  });
}
